import { type RefObject, useEffect, useState } from "react";

function isNumeric(value: any): boolean {
	return !isNaN(value - parseFloat(value));
}

export function useEditableValue<T>(
	type: "text" | "number",
	isEditable: boolean,
	inputValue?: T,
	defaultValue?: T,
	onValueChange?: (value: T) => void,
	inputRef?: RefObject<HTMLInputElement> | RefObject<HTMLTextAreaElement>
) {
	// Hooks
	const [value, setValue] = useState<T | undefined>(inputValue ?? defaultValue);
	const [originalValue, setOriginalValue] = useState(defaultValue || inputValue);
	const [isEditing, setIsEditing] = useState(false);

	// Variables
	const isControlled = inputValue !== undefined;

	const submit = (v: T | undefined) => {
		if (type === "number" && !isNumeric(v)) {
			// TODO error state
			setValue(originalValue);
			return;
		}

		if (v === undefined) {
			return;
		}

		if (onValueChange === undefined) {
			console.error('DataGrid with editable title is missing "on*ValueChange" prop');
		}

		setOriginalValue(v);
		onValueChange?.(v);
		setIsEditing(false);
	};
	const cancel = () => {
		setValue(originalValue);
		setIsEditing(false);
	};

	const handleChange = (value: T) => {
		setValue(value);
	};
	const handleBlur = () => {
		// Blur will appear not to work if inputRef is not passed, and the input is not focused automatically
		submit(value);
		setIsEditing(false);
	};
	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>
	) => {
		switch (e.key) {
			case "Enter":
				if ((e.target as HTMLElement).tagName === "TEXTAREA") {
					break;
				}

				e.preventDefault();
				e.stopPropagation();

				setValue((prev) => {
					let v = prev;
					if (inputRef?.current && inputRef.current.value !== v) {
						// react-aria's useNumberField does not call onChange on keypress, only on blur/increment/decrement
						v = inputRef.current.value as unknown as T;
					}

					submit(v);

					return v;
				});

				break;
			case "Escape":
				e.preventDefault();
				e.stopPropagation();
				cancel();
				break;
			default:
				break;
		}
	};
	const handleEditButtonClick = () => {
		if (!isEditable) {
			return;
		}

		setIsEditing(true);
	};

	useEffect(() => {
		if (!isEditing) {
			return;
		}

		// Focus the input when editing
		inputRef?.current?.focus();
	}, [isEditing]);

	// Update the value with the title changes from the parent for controlled components
	useEffect(() => {
		if (isControlled && inputValue !== value) {
			setValue(inputValue);
		}
	}, [inputValue]);

	return {
		value,
		isEditable,
		isControlled,
		isEditing,
		handleChange,
		handleBlur,
		handleKeyDown,
		handleEditButtonClick,
		submit,
		cancel,
	};
}
